 
import React from "react";
import { api } from "../../api/api";
import {
  CustomFormLabel,
  CustomShrinkTextField,
  CustomTextField,
} from "../formsComponents";
import Autocomplete from "@mui/material/Autocomplete";
import { IUsersAutoSearchByRole } from "./AutoCompleteSearches.types";
import { IUserProfile } from "../../redux/UserProfileList/userProfile.types";
import { CircularProgress, useTheme } from "@mui/material";
import { debounce } from "lodash";

const INITIAL_STATE: IUserProfile = {
  first_name: "",
  last_name: "",
  username: "",
  user_code: "",
  email: "",
  config_json: null,
  role_id: 0,
  status: "ACTIVE",
  user_fact_id: null,
  account: null,
  branch_code: null,
  application_number: null,
  assigned_phone_number: null,
  bank: null,
  bank_name: null,
  bonus_expense_code: null,
  bonus_liability_code: null,
  business_number: null,
  csr: null,
  employment_status: null,
  languages_known: null,
  branch: null,
  broker: null,
  city: null,
  commission_expense_code: null,
  commission_liability_code: null,
  country: null,
  created_by_id: null,
  documents: null,
  insert_ts: null,
  location_city: null,
  manager_id: null,
  marketer: null,
  non_active_firm: null,
  non_active_firm_name: null,
  non_active_firm_registered_with_ribo: null,
  personal_email: null,
  postal_code: null,
  province_or_state: null,
  report_email: null,
  shared_email: [],
  producer_one: null,
  csr_code: null, 
  marketer_code: null,
  producer_one_code: null,
  producer_two: null,
  producer_two_code: null,
  producer_three: null,
  producer_three_code: null,
  date_of_birth: null,
  employee_number: null,
  defaults_role: null,
  department: null,
  fax: null,
  full_name: null,
  hire_date: null,
  home_phone: null,
  job_title: null,
  last_day_at_work: null,
  linkedin_profile: null,
  mobile: null,
  mother_maiden_name: null,
  phone_ext: null,
  reports_to: null,
  signature: null,
  sin: null,
  street_address: null,
  unit_or_suite: null,
  user_type: null,
  notification_email: null,
  photo: null,
  transit: null,
  user_profile_id: 0,
};

export const UsersAutoSearchByRole: React.FC<IUsersAutoSearchByRole> = (props) => {
  const {
    value,
    label,
    selectionBy = "user_fact_id",
    disabled,
    errorMessage,
    placeholder,
    fullWidth = true,
    hasShrinkTextField,
    shrinkTextFieldSize,
    sx
  } = props;

  const [usersList, setUsersList] = React.useState<IUserProfile[]>([]);
  // Holds the user record resolved directly from the hydrated `value`
  // (an id or code). Kept separate from the search results so that typing a
  // new search never discards the ability to render the selected user's name.
  const [selectedUser, setSelectedUser] = React.useState<IUserProfile | null>(
    null
  );
  const [loading, setLoading] = React.useState(false);
  const [searchText, setSearchText] = React.useState("");
  const initialLoad = React.useRef(false);
  const theme = useTheme();

  // ✅ wrap in useCallback to keep same instance
  const fetchUsers = React.useCallback(
    debounce(async (val: string) => {
      try {
        setLoading(true);
        const res = await api.get(
          `/user/get-users?status=ACTIVE&column=username&column=first_name&column=user_code&column=user_fact_id&value=${val}&pageNo=1&itemPerPage=10`
        );
        const data: IUserProfile[] = res.data.data;
        setUsersList([...data]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }, 500), // ⏳ debounce 500ms
    []
  );

  // Resolve the hydrated `value` (a user_fact_id or user_code) to the actual
  // user record so the field shows the user's name instead of a placeholder
  // like "2 (ID)". The generic text search can't be relied on for this because
  // it matches username/first_name/user_code substrings and may not surface
  // the exact record within the paged window. Here we query the specific
  // column so we always get the right user.
  const resolveSelectedUser = React.useCallback(
    async (val: string | number) => {
      try {
        const res = await api.get(
          `/user/get-users?status=ACTIVE&column=${selectionBy}&value=${val}&pageNo=1&itemPerPage=10`
        );
        const data: IUserProfile[] = res.data.data || [];
        const match = data.find((user) =>
          selectionBy === "user_fact_id"
            ? user.user_fact_id === Number(val)
            : user.user_code === val
        );
        if (match) {
          setSelectedUser(match);
        } else {
          // Could not resolve — fall back to a minimal placeholder so the
          // field still reflects the stored value.
          setSelectedUser({
            ...INITIAL_STATE,
            user_code:
              selectionBy === "user_fact_id"
                ? `${val} (ID)`
                : `${val} (Code)`,
            [selectionBy]:
              selectionBy === "user_fact_id" ? Number(val) : val,
          });
        }
      } catch (error) {
        console.error("Error resolving selected user:", error);
      }
    },
    [selectionBy]
  );

  // cleanup on unmount to cancel pending debounce
  React.useEffect(() => {
    return () => {
      fetchUsers.cancel();
    };
  }, [fetchUsers]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchText(val);
    fetchUsers(val);
    initialLoad.current = true;
  };

  const getOptionLabel = (option: IUserProfile) => {
    return `${option.user_code} ${
      option.first_name || option.last_name
        ? `(${option.first_name || ""} ${option.last_name || ""})`
        : ""
    } `;
  };

  const getValue = () => {
    // Prefer a match from the current search results; otherwise fall back to
    // the user resolved directly from the hydrated value so the selected
    // user's name keeps rendering even while/after searching.
    const fromList =
      selectionBy === "user_fact_id"
        ? usersList.find((option) => option.user_fact_id === Number(value))
        : usersList.find((option) => option.user_code === value);
    if (fromList) {
      return fromList;
    }
    if (
      selectedUser &&
      (selectionBy === "user_fact_id"
        ? selectedUser.user_fact_id === Number(value)
        : selectedUser.user_code === value)
    ) {
      return selectedUser;
    }
    return null;
  };

  // Resolve the selected user's name whenever the hydrated value changes
  // (e.g. on initial load or when the record switches). Skipped once the user
  // starts interacting with the field so we don't override their selection.
  React.useEffect(() => {
    if (value) {
      resolveSelectedUser(value);
    } else {
      setSelectedUser(null);
    }
  }, [value, resolveSelectedUser]);

  React.useEffect(() => {
    if (value && !initialLoad.current) {
      setSearchText(value);
      fetchUsers(value); // initial fetch only
    } else if (!value) {
      setSearchText("");
    }
  }, [value, fetchUsers]);

  return (
    <>
      {label && !hasShrinkTextField && (
        <CustomFormLabel>{label}</CustomFormLabel>
      )}
      <Autocomplete
        options={usersList}
        disabled={disabled}
        fullWidth={fullWidth}
        sx={{
          ".MuiOutlinedInput-root": {
            paddingTop: "0px",
            paddingBottom: "0px",
            color: "rgb(38, 38, 38)",
            width: "100%",
          },
          ...sx
        }}
        value={getValue()}
        defaultValue={value}
        getOptionLabel={getOptionLabel}
        onChange={(e, newValue) => {
          if (newValue) {
            setSelectedUser(newValue);
            props.onSelect?.(newValue);
            return;
          }
          setSelectedUser(null);
          props.onSelect?.(INITIAL_STATE);
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input" || !newInputValue) {
            setSearchText(newInputValue || "");
            initialLoad.current = true;
            fetchUsers(newInputValue || "");
          }
        }}
        renderInput={(params) => {
          if (hasShrinkTextField) {
            return (
              <CustomShrinkTextField
                {...params}
                fullWidth
                placeholder={placeholder}
                label={label}
                size={shrinkTextFieldSize}
                error={!!errorMessage}
                helperText={errorMessage}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            );
          }
          return (
            <CustomTextField
              {...params}
              fullWidth
              placeholder={placeholder}
              error={!!errorMessage}
              helperText={errorMessage}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          );
        }}
      />
    </>
  );
};
